import type { ExpressAPI } from '@/api/ExpressApi';
import { getBlobAccessUrl } from '@/hooks/useBlobAccessUrl';
import {
	absolutePublishDateIso,
	absoluteScheduleFrom,
	isPublishNowSentinel,
	resolveOnActivationPublishDate,
} from '@/spaces/chat/panels/scheduleUtils';

import type {
	InstagramPost,
	InstagramPostSchedule,
} from '../../../../../bridge/types/artifact.ts';

export type ExpressChannelInfo = { calendarId: string; channelId: string };

/**
 * True when a scheduled Express post holds media that no longer matches the
 * post's own deck.
 *
 * A slide redraw repairs the stored document minutes after the request, and the
 * bridge cannot reach Express: the Express client lives only in `services/ts/web/src`, so
 * `report_slide_redrawn` writes the document and stops. Express publishes from
 * the media ids it already holds, so nothing else closes this gap and the
 * scheduler publishes a carousel that is still missing the redrawn slide.
 *
 * A publish-now row is excluded on purpose. `syncScheduledExpressPost`
 * recomputes that row's publish date from the clock, so re-syncing one
 * unasked would move the customer's schedule. Their own Save still covers it.
 */
export function scheduledDeckNeedsResync(artifact: InstagramPost): boolean {
	const sched = artifact.schedule;
	if (
		!artifact.express_metadata?.post_id ||
		artifact.status !== 'scheduled' ||
		!sched
	) {
		return false;
	}
	if (isPublishNowSentinel(sched) || !absolutePublishDateIso(sched)) {
		return false;
	}
	const uploaded = artifact.express_metadata.image_urls_at_upload;
	// Nothing recorded means nothing to compare against, so a re-upload would be
	// a guess rather than a repair.
	if (!uploaded) return false;
	const deck = artifact.content.image_urls ?? [];
	return (
		deck.length !== uploaded.length ||
		deck.some((url, i) => url !== uploaded[i])
	);
}

type ArtifactPatcher = {
	patch: (endpoint: string, body: object) => Promise<unknown>;
};

// Upload the post's slides to Express, reusing cached attachment ids for
// slides whose stable URL is unchanged since the last upload.
export async function uploadSlidesToExpress({
	expressApi,
	channelInfo,
	userId,
	artifact,
}: {
	expressApi: ExpressAPI;
	channelInfo: ExpressChannelInfo;
	userId: string;
	artifact: InstagramPost;
}): Promise<string[]> {
	const slideUrls = artifact.content.image_urls ?? [];
	if (slideUrls.length === 0) {
		throw new Error(`Post "${artifact.id}" has no images to schedule`);
	}
	const cachedAttachmentIds = artifact.express_metadata?.attachment_ids ?? [];
	const cachedUploadedUrls =
		artifact.express_metadata?.image_urls_at_upload ?? [];
	return Promise.all(
		slideUrls.map(async (slideUrl, i) => {
			const cachedId = cachedAttachmentIds[i];
			const cachedUrl = cachedUploadedUrls[i];
			if (cachedId && cachedUrl === slideUrl) {
				return cachedId;
			}
			const accessUrl = await getBlobAccessUrl(slideUrl, userId);
			const upload = await expressApi.createMediaWithImageUrl(
				accessUrl,
				channelInfo.calendarId,
			);
			if (!upload.success || !upload.mediaId) {
				throw new Error(upload.error || `Failed to upload slide ${i + 1}`);
			}
			return upload.mediaId;
		}),
	);
}

// Push the post's current content to its already-scheduled Express post:
// re-upload changed slides, update the Express post in place, and persist the
// refreshed upload cache. Express publishes from media it already holds, so a
// content edit persisted only to Cosmos never reaches it without this.
// Returns false when the post has no scheduled Express counterpart to sync.
export async function syncScheduledExpressPost({
	expressApi,
	channelInfo,
	bridgewayApi,
	userId,
	artifact,
}: {
	expressApi: ExpressAPI;
	channelInfo: ExpressChannelInfo;
	bridgewayApi: ArtifactPatcher;
	userId: string;
	artifact: InstagramPost;
}): Promise<boolean> {
	const expressPostId = artifact.express_metadata?.post_id;
	const sched = artifact.schedule;
	if (!expressPostId || artifact.status !== 'scheduled' || !sched) {
		return false;
	}
	const resolveFresh = isPublishNowSentinel(sched);
	// A stored row that is neither a publish-now row nor a readable date, such as
	// a legacy fire_at row or one holding empty on and at fields, gives nothing to
	// send, so leave the Express post alone.
	const storedPublishDate = resolveFresh ? null : absolutePublishDateIso(sched);
	if (!resolveFresh && !storedPublishDate) return false;

	const mediaIds = await uploadSlidesToExpress({
		expressApi,
		channelInfo,
		userId,
		artifact,
	});

	// Express refuses a publish date that has already passed, and the upload
	// above can take a while, so read the clock only once it is done.
	let publishDate: string;
	let resolved: InstagramPostSchedule;
	if (storedPublishDate) {
		publishDate = storedPublishDate;
		resolved = sched;
	} else {
		publishDate = resolveOnActivationPublishDate();
		resolved = absoluteScheduleFrom(new Date(publishDate));
	}

	await expressApi.updatePost(expressPostId, {
		caption: artifact.content.caption,
		channelId: channelInfo.channelId,
		attachmentIds: mediaIds,
		publishDate,
		calendarId: channelInfo.calendarId,
		publishType: 'schedule',
	});
	await bridgewayApi.patch(`/api/user/${userId}/artifacts/${artifact.id}`, {
		schedule: resolved,
		express_metadata: {
			post_id: expressPostId,
			attachment_ids: mediaIds,
			image_urls_at_upload: artifact.content.image_urls ?? [],
			// The artifact PATCH replaces express_metadata whole rather than merging
			// its keys, so every write has to name the target it just sent to
			// Express. Leaving this out erases the date the repeat-schedule guard
			// reads, and the guard then treats the next press as new work.
			publish_date: publishDate,
		},
	});
	return true;
}
